import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import apiClient from './api/client';
import Select from 'react-select';
import { format } from 'date-fns';
import { Edit2, Eye, Search, RotateCcw, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { FaToggleOn, FaToggleOff } from "react-icons/fa";
import CreateAdminUserModal from './components/CreateAdminUserModal';
import EditAdminUserModal from './components/EditAdminUserModal';
import ViewUserDomainsModal from './components/ViewUserDomainsModal';
import { selectStyles } from './helpers';
import { THEME } from './theme/themeColors';

const SEMANTIC_GREEN = '#198754';
const SEMANTIC_RED = '#dc3545';

const pageSizeOptions = [10, 25, 50, 100].map((n) => ({ value: n, label: `${n} / page` }));
const statusOptions = [
  { value: null, label: 'All Status' },
  { value: false, label: 'Enabled' },
  { value: true, label: 'Disabled' }
];

const DEFAULT_FILTERS = { searchTerm: '', roleId: undefined, isDisabled: false };

export default function ManageAdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [statusFilter, setStatusFilter] = useState({ value: false, label: 'Enabled' });
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);

  const [showDomainsModal, setShowDomainsModal] = useState(false);
  const [selectedUserForDomains, setSelectedUserForDomains] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const [showFilters, setShowFilters] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const fetchSeqRef = useRef(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchUsers = useCallback(async (filters, newPage, newPageSize) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const res = await apiClient.get('/api/adminusers/list', {
        params: {
          searchTerm: filters.searchTerm.trim() || undefined,
          roleId: filters.roleId ?? undefined,
          isDisabled: filters.isDisabled ?? undefined,
          page: newPage,
          pageSize: newPageSize,
        }
      });
      if (seq !== fetchSeqRef.current) return;
      setUsers(res.data.items || []);
      setTotalCount(res.data.totalCount ?? res.data.items?.length ?? 0);
      setPage(newPage);
      setPageSize(newPageSize);
      setAppliedFilters(filters);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      toast.error("Failed to load users");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
    fetchUsers(DEFAULT_FILTERS, 1, pageSize);
  }, []);

  const loadRoles = async () => {
    try {
      const res = await apiClient.get('/api/userroles');
      setRoles(res.data.map(r => ({ value: r.id, label: r.text })));
    } catch (err) {
      toast.error("Failed to load roles");
    }
  };

  const roleFilterOptions = [{ value: null, label: 'All Roles' }, ...roles];

  const handleSearch = () => {
    fetchUsers(
      { searchTerm, roleId: selectedRole?.value ?? undefined, isDisabled: statusFilter.value },
      1,
      pageSize
    );
  };

  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page || loading) return;
    fetchUsers(appliedFilters, newPage, pageSize);
  };

  const handlePageSizeChange = (opt) => {
    fetchUsers(appliedFilters, 1, opt.value);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedRole(null);
    setStatusFilter({ value: false, label: 'Enabled' });
    fetchUsers(DEFAULT_FILTERS, 1, pageSize);
  };

  const refreshWithCurrentFilters = () => {
    fetchUsers(appliedFilters, page, pageSize);
  };

  const handleToggle = async (userId, currentDisabled) => {
    const currentDomain = sessionStorage.getItem("selectedDomain");
    try {
      await apiClient.post('/api/adminusers/toggle', {
        userId,
        disabled: !currentDisabled,
        domain: currentDomain
      });

      setUsers(prev => prev.map(u =>
        u.szUserId === userId ? { ...u, btIsDisabled: !currentDisabled } : u
      ));

      toast.success(!currentDisabled ? "User Disabled" : "User Enabled");
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  const openCreateModal = () => setShowCreateModal(true);

  const openEditModal = (user) => {
    setSelectedUserForEdit(user);
    setShowEditModal(true);
  };

  const openDomainsModal = (user) => {
    setSelectedUserForDomains(user);
    setShowDomainsModal(true);
  };

  const toolbarBtn = {
    height: '28px',
    width: isMobile ? '36px' : 'auto',
    padding: isMobile ? '0' : '0 12px',
  };

  const pagerBtn = (disabled) => ({
    padding: isMobile ? '4px 8px' : '6px 10px',
    borderRadius: 6, border: `1px solid ${THEME.alpha(0.3)}`,
    background: disabled ? '#f3f4f6' : '#fff',
    color: disabled ? '#9ca3af' : THEME.primaryHover,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: isMobile ? '11px' : '12px'
  });

  return (
    <>
      <Toaster position="top-right" />

      <style>{`
        .tbl-compact { --bs-table-hover-bg: ${THEME.alpha(0.06)}; }
        .tbl-compact td, .tbl-compact th { padding: 4px 8px !important; vertical-align: middle; }
        .tbl-compact thead th { background: #f8f9fa; font-size: 11.5px; font-weight: 600; color: #495057; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #dee2e6; white-space: nowrap; }
        .tbl-compact tbody tr { height: 36px; }
        .tbl-compact tbody tr:hover > td { background-color: ${THEME.alpha(0.06)}; }
        .form-control-xs { height: 30px !important; font-size: 12px !important; padding: 2px 8px !important; }
        .form-control-xs:focus { border-color: ${THEME.primary}; box-shadow: 0 0 0 0.2rem ${THEME.alpha(0.2)}; }
        .filter-label { font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; display: block; }
        .mau-link { border: none; background: transparent; color: ${THEME.primaryHover}; font-weight: 500; padding: 0; cursor: pointer; text-decoration: none; }
        .mau-link:hover, .mau-link:focus-visible { text-decoration: underline; }
        .domain-view-link { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; }
      `}</style>

      <div className="bg-white" style={{ height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
        <div className="card shadow-sm border-0" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {showFilters && (
            <div className="card-body py-2 px-3" style={{ flex: '0 0 auto' }}>
              <div className="d-flex justify-content-between align-items-end gap-3 flex-wrap">

                <div className="d-flex gap-2 align-items-end flex-wrap">

                  <div style={{ width: '260px' }}>
                    <label className="filter-label" htmlFor="mau-search">Search Name</label>
                    <input
                      id="mau-search"
                      type="text"
                      className="form-control form-control-xs"
                      placeholder="Search Name..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      autoComplete="off"
                    />
                  </div>

                  <div style={{ width: '200px' }}>
                    <label className="filter-label" htmlFor="mau-role">Role</label>
                    <Select
                      inputId="mau-role"
                      options={roleFilterOptions}
                      value={selectedRole}
                      onChange={setSelectedRole}
                      placeholder="Select Role..."
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div style={{ width: '160px' }}>
                    <label className="filter-label" htmlFor="mau-status">Status</label>
                    <Select
                      inputId="mau-status"
                      options={statusOptions}
                      value={statusOptions.find(o => o.value === statusFilter.value)}
                      onChange={setStatusFilter}
                      placeholder="Select Status..."
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <button
                    className="btn btn-sm btn-theme d-flex align-items-center justify-content-center"
                    style={toolbarBtn}
                    onClick={handleSearch}
                    disabled={loading}
                    title="Search"
                    aria-label="Search"
                  >
                    {isMobile ? <Search size={15} /> : 'Search'}
                  </button>

                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
                    style={toolbarBtn}
                    onClick={handleReset}
                    disabled={loading}
                    title="Reset"
                    aria-label="Reset"
                  >
                    {isMobile ? <RotateCcw size={15} /> : 'Reset'}
                  </button>

                  <button
                    className="btn btn-sm btn-theme d-flex align-items-center justify-content-center"
                    style={toolbarBtn}
                    onClick={openCreateModal}
                    title="Create New User"
                    aria-label="Create New User"
                  >
                    {isMobile ? <Plus size={16} /> : 'Create New User'}
                  </button>

                </div>

                <button
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 d-md-none ms-auto"
                  style={{
                    height: '28px',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                  onClick={() => setShowFilters(false)}
                  title="Hide Filters"
                >
                  Hide Filters <ChevronUp size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
            {!showFilters && (
              <button
                className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
                style={{ fontSize: '12px', height: '28px' }}
                onClick={() => setShowFilters(true)}
              >
                Show Filters <ChevronDown size={14} />
              </button>
            )}
          </div>

          <div className="table-responsive" style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>

            <table className="table table-hover mb-0 tbl-compact d-none d-md-table" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky-top">
                <tr>
                  <th style={{ width: '22%' }}>User Name</th>
                  <th style={{ width: '15%' }}>Role</th>
                  <th style={{ width: '22%' }}>Email</th>
                  <th style={{ width: '13%' }}>Created On</th>
                  <th style={{ width: '13%' }}>Active Domain</th>
                  <th style={{ width: '140px' }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-4"><span className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} role="status" /></td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>No users found</td></tr>
                ) : (
                  users.map(user => (
                    <tr key={user.szUserId}>
                      <td style={{ fontSize: '12px' }}>
                        <button
                          className="mau-link text-start"
                          style={{ fontSize: '12px' }}
                          onClick={() => openEditModal(user)}
                        >
                          {user.szUserName}
                        </button>
                      </td>
                      <td style={{ fontSize: '12px' }}>{user.szUserRole || user.userRoleName}</td>
                      <td style={{ fontSize: '12px' }}>{user.szEmail}</td>
                      <td style={{ fontSize: '12px' }}>
                        {user.dtCreated ? format(new Date(user.dtCreated), 'dd-MMM-yy') : '—'}
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        <button
                          className="mau-link domain-view-link"
                          onClick={() => openDomainsModal(user)}
                          title="View active domains"
                        >
                          Click to view
                        </button>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm me-2 p-1"
                          style={{ border: 'none', background: 'transparent', color: THEME.primaryHover }}
                          onClick={() => openEditModal(user)}
                          aria-label={`Edit ${user.szUserName}`}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-sm text-white px-3 py-1"
                          style={{
                            backgroundColor: user.btIsDisabled ? SEMANTIC_GREEN : SEMANTIC_RED,
                            fontSize: '11px',
                            minWidth: '75px'
                          }}
                          onClick={() => handleToggle(user.szUserId, user.btIsDisabled)}
                        >
                          {user.btIsDisabled ? 'Enable' : 'Disable'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/*  Card View */}
            <div className="d-md-none" style={{ padding: '0 12px' }}>
              {loading ? (
                <div className="text-center py-4"><span className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} role="status" /></div>
              ) : users.length === 0 ? (
                <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>No users found</div>
              ) : (
                users.map(user => (
                  <div key={user.szUserId} style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '14px',
                    marginBottom: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontSize: '14.5px' }}>
                          <strong>USER NAME:</strong>{' '}
                          <button className="mau-link text-start"
                            style={{ fontSize: '14.5px' }}
                            onClick={() => openEditModal(user)}
                          >
                            {user.szUserName}
                          </button>
                        </div>
                        <div style={{ fontSize: '14px' }}><strong>ROLE:</strong> {user.szUserRole || user.userRoleName}</div>
                      </div>
                      <button
                        className="btn btn-sm d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: user.btIsDisabled ? SEMANTIC_RED : SEMANTIC_GREEN,
                          color: 'white',
                          fontSize: '13px',
                          width: '32px',
                          height: '26px',
                          padding: '0',
                          border: 'none'
                        }}
                        onClick={() => handleToggle(user.szUserId, user.btIsDisabled)}
                        aria-label={`${user.btIsDisabled ? 'Enable' : 'Disable'} ${user.szUserName}`}
                      >
                        {user.btIsDisabled ? <FaToggleOff size={16} /> : <FaToggleOn size={16} />}
                      </button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '14px' }}>
                        <strong>EMAIL:</strong> {user.szEmail}
                      </div>
                      <button
                        className="btn btn-sm"
                        style={{ color: THEME.primaryHover }}
                        onClick={() => openEditModal(user)}
                        aria-label={`Edit ${user.szUserName}`}
                      >
                        <Edit2 size={15} />
                      </button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '14px' }}>
                        <strong>CREATED:</strong> {user.dtCreated ? format(new Date(user.dtCreated), 'dd-MMM-yy') : '—'}
                      </div>
                      <button
                        className="mau-link domain-view-link"
                        onClick={() => openDomainsModal(user)}
                        style={{ fontSize: '13px', padding: '6px 12px' }}
                        title="View Domains"
                        aria-label={`View domains for ${user.szUserName}`}
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {!loading && users.length > 0 && (
            <div style={{
              flex: '0 0 auto',
              padding: isMobile ? '2px 10px 6px 10px' : '4px 14px 8px 14px',
              borderTop: '1px solid #e5e7eb', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', background: '#fafafa',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              gap: isMobile ? '6px' : '10px',
            }}>
              <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                {isMobile
                  ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} / ${totalCount}`
                  : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
              </span>

              <div style={{ display: 'flex', gap: isMobile ? '2px' : '4px', alignItems: 'center' }}>
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1 || loading}
                  style={pagerBtn(page === 1)}
                  aria-label="Previous page"
                >{isMobile ? '‹' : '‹ Prev'}</button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => {
                    if (totalPages <= 7) return true;
                    if (p === 1 || p === totalPages) return true;
                    if (p >= page - 1 && p <= page + 1) return true;
                    return false;
                  })
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: isMobile ? '11px' : '12px', padding: '0 4px' }}>...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p)}
                        disabled={loading}
                        aria-current={page === p ? 'page' : undefined}
                        style={{
                          width: isMobile ? '26px' : '30px',
                          height: isMobile ? '26px' : '30px',
                          borderRadius: 6,
                          border: `1px solid ${page === p ? THEME.primary : THEME.alpha(0.3)}`,
                          background: page === p ? THEME.primary : '#fff',
                          color: page === p ? THEME.onPrimary : THEME.primaryHover,
                          fontWeight: page === p ? 700 : 400, cursor: 'pointer',
                          fontSize: isMobile ? '11px' : '12px'
                        }}
                      >{p}</button>
                    )
                  )
                }

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages || loading}
                  style={pagerBtn(page === totalPages)}
                  aria-label="Next page"
                >{isMobile ? '›' : 'Next ›'}</button>
              </div>

              <div style={{ width: isMobile ? '85px' : '120px', minWidth: isMobile ? '85px' : '120px' }}>
                <Select
                  options={pageSizeOptions}
                  value={pageSizeOptions.find(o => o.value === pageSize)}
                  onChange={handlePageSizeChange}
                  isSearchable={false}
                  isDisabled={loading}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  classNamePrefix="react-select"
                  aria-label="Rows per page"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateAdminUserModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        roles={roles}
        onSuccess={refreshWithCurrentFilters}
      />

      <EditAdminUserModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={selectedUserForEdit}
        roles={roles}
        onSuccess={refreshWithCurrentFilters}
      />

      <ViewUserDomainsModal
        show={showDomainsModal}
        onClose={() => setShowDomainsModal(false)}
        user={selectedUserForDomains}
      />
    </>
  );
}